<template>
    <div
        v-if="status !== STATUS.ERROR"
        class="parse-status-button-component"
        ref="root"
    >
        <button
            v-if="status !== STATUS.NO_DRAFT"
            type="button"
            class="button"
            @click="handleClick"
            :disabled="isDisabled"
        >
            登録中の契約一覧
            <div v-if="status !== STATUS.NO_PROCESS" class="wrapper">
                <div class="barBox">
                    <span v-if="status === STATUS.IN_PROCESS" class="txt">
                        解析中
                        <span class="count"> {{ progressText }}件</span>
                    </span>
                    <span
                        v-if="status === STATUS.IN_PROCESS"
                        class="bar"
                    ></span>
                    <span
                        v-if="status === STATUS.COMPLETE"
                        class="txt txt-check"
                        >解析完了</span
                    >
                </div>
            </div>
        </button>
    </div>
    <div v-else class="alert alert-danger text-left" role="alert">
        解析ステータスの取得に失敗しました。画面を再度読み込みして改善しない場合はお問い合わせください。
    </div>
</template>

<style scoped lang="scss">
.button {
    display: block;
    width: 100%;
    padding: 8px;
    margin-bottom: 8px;
    background-color: #2ac1a1;
    color: white;
    font-weight: 400;
    border: none;
    border-radius: 8px;
    line-height: 1 !important;

    &:hover {
        background-color: #24a58a;
        transition: 0.5s;
    }
}

.wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 24px;
    color: #000000;
    margin-top: 8px;
}

.barBox {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    border-radius: 10px;
    height: 24px;
    position: relative;
    background-color: #ffffff;
}

.txt {
    height: 24px;
    box-sizing: border-box;
    padding: 3px 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 11;

    .count {
        color: #fd7abd;
    }

    &-check::before {
        font-family: "Font Awesome 5 Free";
        content: "\f058";
        font-size: 13px;
        font-weight: 900;
        color: #00c851;
        margin-right: 3px;
    }
}

.bar {
    width: 100%;
    height: 100%;
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 10px;
    z-index: 10;

    /*stripe*/
    background-size: 400% 100%;
    background-repeat: repeat-x;
    background-color: rgba(255, 255, 255, 1);
    background-image: repeating-linear-gradient(
        135deg,
        transparent,
        transparent 10px,
        rgba(135, 222, 216, 1) 10px,
        rgba(135, 222, 216, 1) 18px
    );
    animation: loading 40s linear infinite;
}

@keyframes loading {
    0% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0% 50%;
    }
}
</style>
<script>
import { ref, onMounted } from "vue";

const ENDPOINT = "/client/contract_file_parse/status";
const INTERVAL = 15000; // 15秒
const MAX_ATTEMPTS = 5; // リクエスト上限
const STATUS = {
    ERROR: 0, // エラー
    NO_DRAFT: 1, // 下書きなし
    NO_PROCESS: 2, // 解析対象なし
    IN_PROCESS: 3, // 解析中
    COMPLETE: 4, // 解析完了
};

export default {
    props: {
        href: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const status = ref(STATUS.NO_DRAFT);
        const progressText = ref(0);
        const isDisabled = ref(false);
        const intervalId = ref(null);
        const intervalCount = ref(0);
        const handleClick = () => {
            isDisabled.value = true;
            window.location.href = props.href;
        };

        const fetchStatus = async () => {
            try {
                const response = await fetch(ENDPOINT);
                if (!response.ok) {
                    throw new Error("request failed.");
                }
                const data = await response.json();

                // ステータス更新
                status.value = calcStatus(data);
                if (status.value === STATUS.IN_PROCESS) {
                    progressText.value = createProgressText(data);
                }
            } catch (_error) {
                status.value = STATUS.ERROR;
                stopInterval();
            }
        };

        const calcStatus = (data) => {
            // 下書きなし
            if (!data.draftExists) {
                return STATUS.NO_DRAFT;
            }

            // 解析対象なし
            if (data.fileDraftCount === 0) {
                return STATUS.NO_PROCESS;
            }

            // 解析中 or 解析完了
            const completeCount = data.errorCount + data.successCount;
            if (completeCount < data.fileDraftCount) {
                return STATUS.IN_PROCESS;
            } else {
                return STATUS.COMPLETE;
            }
        };

        const createProgressText = (data) => {
            const completeCount = data.successCount + data.errorCount;
            return `${completeCount}/${data.fileDraftCount}`;
        };

        const refresh = async () => {
            if (intervalCount.value < MAX_ATTEMPTS) {
                await fetchStatus();
                intervalCount.value += 1;
            } else {
                stopInterval();
                if (status.value === STATUS.IN_PROCESS) {
                    status.value = STATUS.COMPLETE; // タイムアウト時は解析完了とする
                }
            }
        };

        const stopInterval = () => {
            if (intervalId.value) {
                clearInterval(intervalId.value);
            }
        };

        onMounted(async () => {
            await fetchStatus(); // 初回実行を待つ
            if (status.value === STATUS.IN_PROCESS) {
                intervalId.value = setInterval(refresh, INTERVAL);
            }
        });

        return {
            status,
            progressText,
            STATUS,
            handleClick,
            isDisabled,
        };
    },
};
</script>
